import * as Icons from '@hose/eui-icons'
import { MoneyObject } from '../../types/dataMatched';

// 报销状态枚举
export enum ConsumptionStatus {
    DRAFT = 'DRAFT',         // 待报销
    PROCESSING = 'PROCESSING',   // 报销中
    PROCESSED = 'PROCESSED',     // 已报销
}

/**
 * 原始凭证实体
 */
export interface OriginalVoucher {
    fileId: string                     // 文件id
    fileName: string                   // 文件名
    key: string                         // 凭证key
}

export interface PreprocessState {
    fileExtract: any | null
    requisitionReason: any | null
}

// 消费一级分类枚举
export enum ConsumptionCategory {
    TRAVEL = '差旅',              // 差旅
    HOSPITALITY = '招待',         // 招待
    OPERATION = '办公运营',       // 办公运营
    EMPLOYEE = '员工',            // 员工
}

// 消费记录类型
export interface Consumption {
    id?: string
    consumptionCategory: ConsumptionCategory // 一级分类
    consumptionType?: string                  // 二级分类
    consumptionAmount: MoneyObject     // 消费金额
    consumptionDate: string       // 消费日期
    consumptionDescription?: string // 消费描述
    createTime?: string            // 创建时间
    updateTime?: string            // 更新时间
    invoices?: string[]               // 发票列表
    originalVouchers?: OriginalVoucher[] // 原始凭证列表
}

export interface ConsumptionDetail {
    id: string
    ownerId: string
    consumptionCategory: ConsumptionCategory
    consumptionType: string
    consumptionDate: string
    invoiceDate: string | null
    merchantName: string | null
    consumptionDescription: string
    consumptionItemId: string | null
    requisitionId: string | null
    corporationId: string
    flowId: string | null
    flowCode: string | null
    detailId: string | null
    state: ConsumptionStatus
    source: string
    active: boolean
    createTime: string
    updateTime: string
    invoices: string[]
    originalVouchers: OriginalVoucher[]
    consumptionAmount: MoneyObject
    preprocessState: PreprocessState
}

// ==================== 消费包（事项）相关 ====================
// AI 消费包建议类型
export interface ConsumptionPackage {
    id: string                      // 主键ID
    title: string                   // 消费事项标题
    description: string            // 消费事项描述
    requisitionId: string          // 申请事项ID
    ownerId: string                 // 用户ID
    corporationId: string           // 租户ID
    createTime: string             // 创建时间
    updateTime: string             // 更新时间
    consumptions: ConsumptionDetail[] // 消费明细列表
}



export const ConsumptionCategoryObject: Record<ConsumptionCategory, { icon: keyof typeof Icons, color: string, bgColor: string }> = {
    [ConsumptionCategory.TRAVEL]: {
        icon: 'FilledGeneralTravelConfiguration',
        color: 'var(--eui-decorative-neu-500)',
        bgColor: 'var(--eui-decorative-neu-100)'
    },
    [ConsumptionCategory.HOSPITALITY]: {
        icon: 'FilledGeneralGoblet',
        color: 'var(--eui-decorative-neu-500)',
        bgColor: 'var(--eui-decorative-neu-100)'
    },
    [ConsumptionCategory.OPERATION]: {
        icon: 'FilledGeneralJobs',
        color: 'var(--eui-decorative-neu-500)',
        bgColor: 'var(--eui-decorative-neu-100)'
    },
    [ConsumptionCategory.EMPLOYEE]: {
        icon: 'FilledGeneralGroup',
        color: 'var(--eui-decorative-neu-500)',
        bgColor: 'var(--eui-decorative-neu-100)'
    }
}

// 消费二级分类配置 - 按一级分类组织
export const ConsumptionTypeOptions: Record<ConsumptionCategory, { value: string; label: string; icon: keyof typeof Icons, color: string, bgColor: string }[]> = {
    [ConsumptionCategory.TRAVEL]: [
        { value: '飞机', label: '飞机', icon: 'FilledGeneralFlight', color: 'var(--eui-function-info-500)', bgColor: 'var(--eui-function-info-100)' },
        { value: '火车/高铁', label: '火车/高铁', icon: 'FilledGeneralTrain', color: 'var(--eui-primary-pri-500)', bgColor: 'var(--eui-primary-pri-100)' },
        { value: '轮船/汽车', label: '轮船/汽车', icon: 'FilledGeneralSailboat', color: 'var(--eui-function-warning-500)', bgColor: 'var(--eui-function-warning-100)' },
        { value: '酒店住宿', label: '酒店住宿', icon: 'FilledGeneralBuilding', color: 'var(--eui-decorative-pur-500)', bgColor: 'var(--eui-decorative-pur-100)' },
        { value: '市内交通', label: '市内交通', icon: 'FilledGeneralCar', color: 'var(--eui-function-warning-500)', bgColor: 'var(--eui-function-warning-100)' },
        { value: '出差补助', label: '出差补助', icon: 'FilledGeneralWallet', color: 'var(--eui-decorative-cyan-500)', bgColor: 'var(--eui-decorative-cyan-100)' },
        { value: '其他差旅', label: '其他差旅', icon: 'FilledGeneralTravelConfiguration', color: 'var(--eui-decorative-neu-500)', bgColor: 'var(--eui-decorative-neu-100)' },
    ],
    [ConsumptionCategory.HOSPITALITY]: [
        { value: '招待餐费', label: '招待餐费', icon: 'FilledGeneralKnifeFork', color: 'var(--eui-function-danger-500)', bgColor: 'var(--eui-function-danger-100)' },
        { value: '礼品费', label: '礼品费', icon: 'FilledGeneralInvoice2', color: 'var(--eui-decorative-yel-500)', bgColor: 'var(--eui-decorative-yel-100)' },
        { value: '招待活动', label: '招待活动', icon: 'FilledGeneralGoblet', color: 'var(--eui-function-success-500)', bgColor: 'var(--eui-function-success-100)' },
        { value: '招待交通', label: '招待交通', icon: 'FilledGeneralDriver', color: 'var(--eui-primary-pri-500)', bgColor: 'var(--eui-primary-pri-100)' },
    ],
    [ConsumptionCategory.OPERATION]: [
        { value: '办公用品', label: '办公用品', icon: 'FilledGeneralPrinter', color: 'var(--eui-function-info-500)', bgColor: 'var(--eui-function-info-100)' },
        { value: '通讯/邮寄', label: '通讯/邮寄', icon: 'FilledGeneralMessageSend', color: 'var(--eui-primary-pri-400)', bgColor: 'var(--eui-primary-pri-100)' },
        { value: '软件/云服务', label: '软件/云服务', icon: 'FilledGeneralTool', color: 'var(--eui-decorative-pur-500)', bgColor: 'var(--eui-decorative-pur-100)' },
        { value: '其他运营', label: '其他运营', icon: 'FilledGeneralJobs', color: 'var(--eui-decorative-neu-500)', bgColor: 'var(--eui-decorative-neu-100)' },
    ],
    [ConsumptionCategory.EMPLOYEE]: [
        { value: '工作餐补', label: '工作餐补', icon: 'FilledGeneralKnifeFork', color: 'var(--eui-function-warning-500)', bgColor: 'var(--eui-function-warning-100)' },
        { value: '团队建设', label: '团队建设', icon: 'FilledGeneralGroup', color: 'var(--eui-function-success-500)', bgColor: 'var(--eui-function-success-100)' },
        { value: '培训/会议', label: '培训/会议', icon: 'FilledGeneralChampion', color: 'var(--eui-primary-pri-500)', bgColor: 'var(--eui-primary-pri-100)' },
        { value: '节日福利', label: '节日福利', icon: 'FilledGeneralActivity', color: 'var(--eui-decorative-mag-500)', bgColor: 'var(--eui-decorative-mag-100)' },
        { value: '交通补助', label: '交通补助', icon: 'FilledGeneralGiftBag', color: 'var(--eui-decorative-cyan-500)', bgColor: 'var(--eui-decorative-cyan-100)' },
    ],
}